import React, { useState, useRef, useEffect } from 'react';
import { Icon, Modal, IconButton } from '@livecommerce/design-system';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import ProfileMenu, { MODAL_NAME } from 'components-v3/ProfileMenu/index.jsx';
import ProfileImage from 'components-v3/ProfileImage/index.jsx';
import AccountSettings from 'components-v3/AccountSettings/index.jsx';
import CloseConfirmationDialog from 'components-v3/AccountSettings/CloseConfirmationDialog.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import useNavigation from 'hooks/useNavigation/useNavigation.js';
import ProductsNavigationListItem from 'components-v3/ProductsNavigationListItem/index.jsx';
import { useModal } from 'hooks/index.js';
import useClickOutside from 'hooks/useClickOutside.js';
import NavigationDropdown from 'components-v3/NavigationDropdown/index.jsx';
import { useLoadingContext } from 'components/ContextLoader/index.jsx';
import {
  AbsoluteWrapper,
  SidebarContainer,
  NavigationContainer,
  SidebarContentArea,
  InteractiveElementContainer,
  InteractiveElementButton,
  ScrollableNavigationItems,
  ScrollArrowButton,
  SkeletonNavigationListItem,
  LanguageSelector,
} from './styles.js';
import SkeletonCell from 'components-v3/List/cells/SkeletonCell.jsx';
import { LanguageSelectorOpenModal } from 'services/tracking/dataClickIdEventNames.js';

const arrayOfThree = new Array(3).fill(null);

const Sidebar = ({ forwardedRef, ...props }) => {
  const { t, language, isLatin } = useLocalization();
  const {
    isSidebarExpanded,
    setSelectedNavigationItemTitle,
  } = useNavigationFrameContext();

  const { isLoading } = useLoadingContext();
  const { navigationElements } = useNavigation();
  const { isOpen: isProfileModalOpen, open: openProfileModal, close: closeProfileModal } = useModal();
  const { isOpen: isConfirmDialogOpen, open: openConfirmDialog, close: closeConfirmDialog } = useModal();
  const [isProfileMenuOpened, setIsProfileMenuOpened] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [profileModalIsClosable, setProfileModalIsClosable] = useState(true);
  const { features: { userLanguageEnabled } = {} } = useFeatureContext();

  const navigationRef = useRef(null);
  const scrollRef = useRef(null);
  const { ref } = useClickOutside(() => {
    if (!isSidebarExpanded) return;
    setSelectedNavigationItemTitle();
  });

  useEffect(() => {
    const checkScrollable = () => {
      const { scrollHeight, clientHeight } = scrollRef.current;
      setIsScrollable(scrollHeight > clientHeight);
    };

    checkScrollable();
    window.addEventListener('resize', checkScrollable);

    return () => {
      window.removeEventListener('resize', checkScrollable);
    };
  }, [scrollRef]);

  const handleProfileMenuClick = () => {
    setIsProfileMenuOpened((prevValue) => {
      const nextOpenedState = !prevValue;
      trackingService.track({
        eventType: `sidebar-profile-menu:${nextOpenedState ? 'open' : 'close'}`,
      });
      return nextOpenedState;
    });
  };

  const handleProfileModalClose = () => {
    if (!profileModalIsClosable) {
      openConfirmDialog();
      return;
    }
    trackingService.track({
      eventType: 'profile-modal:close',
    });
    closeProfileModal();
  };

  const handleConfirmDiscardClick = () => {
    trackingService.track({
      eventType: 'profile-modal:close:without-saving',
    });
    closeConfirmDialog();
    closeProfileModal();
  };

  const handleProfileDropdownClickOutside = () => setIsProfileMenuOpened(false);

  const handleScrollToBottom = () => {
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  const triggerOpenProfileModal = () => {
    trackingService.trackClick({ clickId: LanguageSelectorOpenModal });
    openProfileModal();
  };

  return (
    <SidebarContainer ref={forwardedRef} {...props}>
      <div ref={ref}>
        <SidebarContentArea
          isSidebarExpanded={isSidebarExpanded}
        >
          <ScrollableNavigationItems>
            <AbsoluteWrapper ref={scrollRef}>
              <NavigationContainer ref={navigationRef} scollable={isScrollable}>
                {isLoading &&
                  arrayOfThree.map((_, index) => (
                    <SkeletonNavigationListItem key={index}>
                      <SkeletonCell width={48} height={48} borderRadius={6} />
                      <SkeletonCell width={48} height={12} borderRadius={12} opacityVariant="dark" />
                    </SkeletonNavigationListItem>
                  ))}

                {!isLoading &&
                  navigationElements.map(
                    ({ title, dataTestId, icon, route, routeParams, exact, url, disabled, submenu, align, flair, numberIndicators }) => (
                      <ProductsNavigationListItem
                        key={title}
                        title={title}
                        icon={icon}
                        flair={flair}
                        numberIndicators={numberIndicators}
                        route={route}
                        routeParams={routeParams}
                        exact={exact}
                        url={url}
                        disabled={disabled}
                        submenu={submenu}
                        align={align}
                        dataTestId={dataTestId}
                        data-test-id={`${title}SidebarItem`}
                      />
                    ),
                  )}
              </NavigationContainer>
            </AbsoluteWrapper>
          </ScrollableNavigationItems>
          {isScrollable && (
            <ScrollArrowButton
              iconName="chevron"
              iconDirection="down"
              variant="transparent"
              theme="dark"
              role="button"
              aria-label={t('genericLabels.scrollToBottom')}
              onClick={handleScrollToBottom}
            />
          )}
          <InteractiveElementContainer>
            {isLoading && <SkeletonCell width={48} height={48} borderRadius={6} />}
            {!isLoading && (
              <>
                {userLanguageEnabled && (
                  <>
                    <LanguageSelector
                      isLatin={isLatin}
                      type="button"
                      active={false}
                      backgroundColor="transparent"
                      color="white"
                      activeBackgroundColor="transparent"
                      activeColor="white"
                      aria-label="changeLanguage"
                      onClick={triggerOpenProfileModal}
                    >
                      {language}
                    </LanguageSelector>
                    <Modal
                      width={1000}
                      name={MODAL_NAME}
                      isModalOpen={isProfileModalOpen}
                      onClose={handleProfileModalClose}
                      title={t('profile.profileModalTitle')}
                      dataTestId="accountSettingsModal"
                    >
                      <AccountSettings
                        setIsClosable={setProfileModalIsClosable}
                        isClosable={profileModalIsClosable}
                        selectedSectionId="language"
                      />
                    </Modal>
                    <CloseConfirmationDialog
                      handleConfirmCancelClick={closeConfirmDialog}
                      handleConfirmDiscardClick={handleConfirmDiscardClick}
                      isConfirmDialogOpen={isConfirmDialogOpen}
                    />
                  </>
                )}
                <NavigationDropdown
                  isOpened={isProfileMenuOpened}
                  onClickOutside={handleProfileDropdownClickOutside}
                  isControlled
                  hoverBackgroundColor="transparent"
                  dropdownContent={<ProfileMenu setIsOpened={setIsProfileMenuOpened} />}
                >
                  <InteractiveElementButton
                    type="button"
                    active={isProfileMenuOpened}
                    backgroundColor="transparent"
                    color="white"
                    activeColor="white"
                    activeBackgroundColor="transparent"
                    aria-label="toggleProfileMenu"
                    onClick={handleProfileMenuClick}
                    data-test-id="profileMenuDropdown"
                  >
                    <ProfileImage width={48} height={48} alt="User Avatar icon" />
                  </InteractiveElementButton>
                </NavigationDropdown>
              </>
            )}
          </InteractiveElementContainer>
        </SidebarContentArea>
      </div>
    </SidebarContainer>
  );
};

Sidebar.propTypes = {};

export default React.forwardRef((props, ref) => <Sidebar {...props} forwardedRef={ref} />);
